import { createRouter, createWebHistory } from "vue-router";
// HomeView is the landing/prerendered root, so keep it in the entry bundle.
// Every other route is lazy-loaded as its own chunk (code-splitting) so the
// initial download only carries the home page instead of all 15 views.
import HomeView from "../views/HomeView.vue";

// Site-wide SEO defaults (mirror public/index.html)
const SITE_URL = "https://www.africahrsummit.org";
const SITE_NAME = "Africa Human Resource Summit";
const DEFAULT_TITLE = "Africa Human Resource Summit";
const DEFAULT_DESCRIPTION =
  "Africa Human Resource Summit";

const routes = [
  {
    path: "/",
    name: "home",
    component: HomeView,
    meta: {
      title: DEFAULT_TITLE,
      description: DEFAULT_DESCRIPTION,
    },
  },
  {
    path: "/registration",
    name: "joinus",
    component: () => import(/* webpackChunkName: "registration" */ "../views/JoinUs.vue"),
    meta: {
      title: "Register — Africa Human Resource Summit",
      description:
        "Register for Africa Human Resource Summit",
    },
  },
  {
    path: "/checkout",
    name: "checkout",
    component: () => import(/* webpackChunkName: "checkout" */ "../views/CheckoutView.vue"),
    meta: {
      title: "Checkout — AHRS 2026",
      description: DEFAULT_DESCRIPTION,
      noindex: true,
    },
  },
  {
    path: "/documents",
    name: "documents",
    component: () => import(/* webpackChunkName: "documents" */ "../views/DocumentUploadView.vue"),
    meta: {
      title: "Upload Documents — AHRS 2026",
      description: DEFAULT_DESCRIPTION,
      noindex: true,
    },
  },
  {
    path: "/tour",
    name: "tour",
    component: () => import(/* webpackChunkName: "tour" */ "../views/TourView.vue"),
    meta: {
      title: "Heritage Discovery Tour — Tarangire National Park | AHRS 2026",
      description:
        "Extend your AHRS trip with the Heritage Discovery Tour to Tarangire National Park on 22 August 2026 — explore Tanzania's natural heritage after the summit in Arusha.",
    },
  },
  {
    path: "/tour-registration",
    name: "tour-registration",
    component: () => import(/* webpackChunkName: "tour-registration" */ "../views/TourRegistrationView.vue"),
    meta: {
      title: "Tour Registration — AHRS 2026",
      description: DEFAULT_DESCRIPTION,
      noindex: true,
    },
  },
  {
    path: "/accommodation",
    name: "accommodation",
    component: () => import(/* webpackChunkName: "accommodation" */ "../views/AccommodationView.vue"),
    meta: {
      title: "Accommodation & Hotels — AHRS 2026 Arusha",
      description:
        "Where to stay for AHRS 2026 in Arusha, Tanzania. Recommended hotels and booking assistance for summit delegates.",
    },
  },
  // {
  //   path: "/exhibitor",
  //   name: "exhibitor",
  //   component: ExhibitorView,
  // },
  {
    path: "/sponsorship",
    name: "sponsorship",
    component: () => import(/* webpackChunkName: "sponsorship" */ "../views/SponsorshipView.vue"),
    meta: {
      title: "Sponsorship & Partnership — AHRS 2026",
      description:
        "Partner with Africa's HR community. Explore strategic sponsorship and partnership tiers for the 2nd Africa Human Resource Summit 2026 in Arusha, Tanzania.",
    },
  },
  {
    path: "/partners",
    name: "partners",
    component: () => import(/* webpackChunkName: "partners" */ "../views/PartnersView.vue"),
    meta: {
      title: "Partners — AHRS 2026",
      description:
        "AHRS 2026 is endorsed by 21+ partner HR associations across Africa and beyond, including AHRC, THRAPA, SHRM, WFPMA, and WEC.",
    },
  },
  {
    path: "/speakers",
    name: "speakers",
    component: () => import(/* webpackChunkName: "speakers" */ "../views/SpeakersView.vue"),
    meta: {
      title: "Speakers — AHRS 2026",
      description:
        "Meet the voices shaping Africa's future of work — the guest of honour, keynote speakers, moderators, panelists, and facilitators of AHRS 2026.",
    },
  },
  {
    path: "/agenda",
    name: "agenda",
    component: () => import(/* webpackChunkName: "agenda" */ "../views/AgendaView.vue"),
    meta: {
      title: "Agenda & Programme — AHRS 2026",
      description:
        "The full AHRS 2026 programme — masterclass, three-day summit, and heritage tour across 19–23 August 2026 at Mount Meru Hotel, Arusha, Tanzania.",
    },
  },
  {
    path: "/contact",
    name: "contact",
    component: () => import(/* webpackChunkName: "contact" */ "../views/ContactView.vue"),
    meta: {
      title: "Contact — AHRS 2026",
      description:
        "Talk to the AHRS 2026 secretariat. Get in touch about registration, partnership, and delegate support for the Africa Human Resource Summit.",
    },
  },
  {
    path: "/visa",
    name: "visa",
    component: () => import(/* webpackChunkName: "visa" */ "../views/VisaView.vue"),
    meta: {
      title: "Tanzania Visa Guide — AHRS 2026",
      description:
        "Tanzania visa guidance for AHRS 2026 delegates — check visa-on-arrival eligibility, e-Visa steps, visa types and fees, and required documents.",
    },
  },
  {
    path: "/travel",
    name: "travel",
    component: () => import(/* webpackChunkName: "travel" */ "../views/TravelView.vue"),
    meta: {
      title: "Travel & Transfers — AHRS 2026",
      description:
        "Travel logistics for AHRS 2026 — flying into Kilimanjaro (JRO), airport transfers, getting around Arusha, and the Mount Meru Hotel venue.",
    },
  },
  // Catch all other routes and show 404 page
  {
    path: "/:pathMatch(.*)*",
    name: "notfound",
    component: () => import(/* webpackChunkName: "notfound" */ "../views/NotFoundView.vue"),
    meta: {
      title: "Page Not Found — AHRS 2026",
      description: DEFAULT_DESCRIPTION,
      noindex: true,
    },
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else if (to.hash) {
      return {
        el: to.hash,
        behavior: "smooth",
      };
    } else {
      return { top: 0 };
    }
  },
});

// Breadcrumb trail labels for indexable inner pages (Home -> label).
// Home, transactional/noindex routes, and 404 deliberately get no breadcrumb.
const BREADCRUMB_LABELS = {
  joinus: "Register",
  tour: "Heritage Tour",
  accommodation: "Accommodation",
  sponsorship: "Sponsorship",
  partners: "Partners",
  speakers: "Speakers",
  agenda: "Agenda",
  contact: "Contact",
  visa: "Visa Guide",
  travel: "Travel",
};

// --- Per-route SEO head management ---
// Reflects each route's meta into the document head so that (a) Google's
// rendered pass and (b) the Phase 3 prerender build both get correct,
// unique title / description / canonical / Open Graph per page.
function setMeta(selector, attr, value) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(
      selector.startsWith("link") ? "link" : "meta"
    );
    // Derive the identifying attribute from the selector, e.g.
    // 'meta[name="description"]' -> name="description".
    const match = selector.match(/\[(.+?)="(.+?)"\]/);
    if (match) el.setAttribute(match[1], match[2]);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

// Inject (or clear) a BreadcrumbList JSON-LD block for the active route. Runs
// during the prerender build too, so the trail is baked into each static page.
function setBreadcrumb(to) {
  const id = "breadcrumb-jsonld";
  let el = document.getElementById(id);
  const label = BREADCRUMB_LABELS[to.name];

  // No breadcrumb for home, noindex, or unmapped routes — remove a stale one.
  if (!label || to.meta.noindex) {
    if (el) el.remove();
    return;
  }

  const data = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL + "/" },
      // Final crumb omits `item` — Google uses the current page URL.
      { "@type": "ListItem", position: 2, name: label },
    ],
  };

  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

router.afterEach((to) => {
  const title = to.meta.title || DEFAULT_TITLE;
  const description = to.meta.description || DEFAULT_DESCRIPTION;
  const url = SITE_URL + (to.path === "/" ? "/" : to.path);

  document.title = title;
  setMeta('meta[name="description"]', "content", description);
  setMeta('link[rel="canonical"]', "href", url);
  setMeta(
    'meta[name="robots"]',
    "content",
    to.meta.noindex ? "noindex, follow" : "index, follow, max-image-preview:large"
  );

  // Open Graph + Twitter mirror the per-route values.
  setMeta('meta[property="og:title"]', "content", title);
  setMeta('meta[property="og:description"]', "content", description);
  setMeta('meta[property="og:url"]', "content", url);
  setMeta('meta[property="og:site_name"]', "content", SITE_NAME);
  setMeta('meta[name="twitter:title"]', "content", title);
  setMeta('meta[name="twitter:description"]', "content", description);

  setBreadcrumb(to);
});

export default router;
